export type ParticipantColorId =
    | 'red' | 'blue' | 'green' | 'yellow' | 'purple'
    | 'orange' | 'pink' | 'teal' | 'cyan' | 'lime'
    | 'indigo' | 'brown' | 'amber' | 'emerald' | 'rose'
    | 'red_striped' | 'blue_striped' | 'green_striped' | 'purple_striped' | 'orange_striped'
    | 'fallback';

export interface ParticipantColorSchema {
    id: ParticipantColorId;
    bg: string;
    text: string;
    border: string;
    isDark: boolean;
    stripePattern?: string;
}

// Utility to determine if a color is "light" (needs black text) or "dark" (needs white text)
export const isColorLight = (hex: string): boolean => {
    const color = hex.replace('#', '');
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    // Perceptive luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6; // High threshold for safety with neon colors
};

export const PARTICIPANT_COLORS: Record<string, ParticipantColorSchema> = {
    // Sólidos - More vibrant/saturated versions (Neon/Fluor feel)
    red: { id: 'red', bg: '#FF0033', border: '#FF0033', text: '#ffffff', isDark: true },
    blue: { id: 'blue', bg: '#0066FF', border: '#0066FF', text: '#ffffff', isDark: true },
    green: { id: 'green', bg: '#00FF44', border: '#00FF44', text: '#000000', isDark: false },
    yellow: { id: 'yellow', bg: '#FFFF00', border: '#FFFF00', text: '#000000', isDark: false },
    purple: { id: 'purple', bg: '#9900FF', border: '#9900FF', text: '#ffffff', isDark: true },
    orange: { id: 'orange', bg: '#FF6600', border: '#FF6600', text: '#ffffff', isDark: true },
    pink: { id: 'pink', bg: '#FF00FF', border: '#FF00FF', text: '#ffffff', isDark: true },
    teal: { id: 'teal', bg: '#00FFCC', border: '#00FFCC', text: '#000000', isDark: false },
    cyan: { id: 'cyan', bg: '#00FFFF', border: '#00FFFF', text: '#000000', isDark: false },
    lime: { id: 'lime', bg: '#CCFF00', border: '#CCFF00', text: '#000000', isDark: false },
    indigo: { id: 'indigo', bg: '#6600FF', border: '#6600FF', text: '#ffffff', isDark: true },
    brown: { id: 'brown', bg: '#8B4513', border: '#8B4513', text: '#ffffff', isDark: true },
    amber: { id: 'amber', bg: '#FFBF00', border: '#FFBF00', text: '#000000', isDark: false },
    emerald: { id: 'emerald', bg: '#00FF88', border: '#00FF88', text: '#000000', isDark: false },
    rose: { id: 'rose', bg: '#FF0077', border: '#FF0077', text: '#ffffff', isDark: true },

    // Fallback/Default — Using the brand neon pink (#f72d60)
    fallback: { id: 'fallback', bg: '#f72d60', border: '#f72d60', text: '#ffffff', isDark: true }
};

// Genera un patrón a rayas en CSS en base al color base
const createStripedBackground = (baseColor: string, darkColor: string) => {
    return `repeating-linear-gradient(45deg, ${baseColor}, ${baseColor} 6px, ${darkColor} 6px, ${darkColor} 12px)`;
};

// Injection de variables _striped dinámicas basadas en las sólidas
['red', 'blue', 'green', 'purple', 'orange'].forEach(baseId => {
    const base = PARTICIPANT_COLORS[baseId];
    PARTICIPANT_COLORS[`${baseId}_striped`] = {
        ...base,
        id: `${baseId}_striped` as ParticipantColorId,
        // Mantener el fondo base sólido
        bg: base.bg,
        text: base.text,
        isDark: base.isDark,
        // Guardar el patrón a rayas para aplicarlo al borde
        stripePattern: createStripedBackground(base.bg, 'rgba(0,0,0,0.5)')
    };
});

/**
 * Gets the standardized color formatting based on a color ID.
 * Defaults to 'fallback' if undefined or missing.
 */
export const getParticipantColor = (colorId?: string | null, customColor?: string | null): ParticipantColorSchema => {
    if (colorId === 'custom' && customColor) {
        const isDark = !isColorLight(customColor);

        return {
            id: 'fallback',
            bg: customColor,
            border: customColor,
            text: isDark ? '#ffffff' : '#000000',
            isDark: isDark
        } as ParticipantColorSchema;
    }

    if (!colorId || !PARTICIPANT_COLORS[colorId]) {
        return PARTICIPANT_COLORS['fallback'];
    }
    const color = PARTICIPANT_COLORS[colorId];
    // Re-check contrast for safety if bg was updated
    const isLight = isColorLight(color.bg);
    return {
        ...color,
        text: isLight ? '#000000' : '#ffffff',
        isDark: !isLight
    };
};
